import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useGetCreateOrderValues } from "@/services/Orders";
import { t } from "i18next";
import { Grid, Stack } from "@mantine/core";
import { SettingsFormContent, SettingsFormCTAs } from "@/components/pages/Settings/index";
import {
  ContractDeviationFormInput,
  ContractDeviationValidationSchema,
} from "@/components/pages/Settings/validation/contractDeviationValidationSchema";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ContractDeviationType, CurrencyCode } from "@/types";
import {
  createContractDeviationType,
  updateContractDeviationType,
  useGetContractDeviationUnits,
} from "@/services/ContractDeviations";
import { notifications } from "@mantine/notifications";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";
import Select, { transformDataToSelectOption } from "@/components/atoms/Select";
import Heading from "@/components/atoms/Heading";
import Text from "@/components/atoms/Text";
import Divider from "@/components/atoms/Divider";
import TextInput from "@/components/atoms/TextInput";
import NumberInput from "@/components/atoms/NumberInput";
import Group from "@/components/atoms/Group";
import CTA from "@/components/atoms/CTA";
import SegmentedControls from "@/components/atoms/SegmentedControls";
import DeviationsTable from "../../organisms/SettingsTables/DeviationTable";
import { getApiError } from "@/utils/apiErrors";
import DeviationProductSettings from "./DeviationProductSettings";

/** Services are put on contracts; products are sorted out in receiving inspections. */
type DeviationKind = "services" | "products";

const DeviationSettings: React.FC = () => {
  const [selectedContractDeviation, setSelectedContractDeviation] = useState<ContractDeviationType | null>(null);
  const [kind, setKind] = useState<DeviationKind>("services");
  const { currentCompany, isAdminUser } = useAuth();

  const isAdmin = isAdminUser(currentCompany);
  return (
    <Stack data-testid="deviation-settings">
      <Heading order={2} styleOrder={4} tt="uppercase">
        {t("settings:navigation.deviation")}
      </Heading>
      <Group>
        <SegmentedControls
          aria-label={t("settings:deviationSettings.kindSwitch")}
          data-testid="deviation-kind-switch"
          value={kind}
          onChange={(value) => setKind(value as DeviationKind)}
          data={[
            { value: "services", label: t("settings:deviationSettings.servicesTab") },
            { value: "products", label: t("settings:deviationSettings.productsTab") },
          ]}
        />
      </Group>
      {kind === "services" ? (
        <>
          <DeviationForm
            companyId={currentCompany?.companyId}
            isAdmin={isAdmin}
            selectedContractDeviation={selectedContractDeviation}
            setSelectedContractDeviation={setSelectedContractDeviation}
          />
          <DeviationsTable
            isAdmin={isAdmin}
            companyId={currentCompany?.companyId}
            setSelectedContractDeviation={setSelectedContractDeviation}
          />
        </>
      ) : (
        <DeviationProductSettings companyId={currentCompany?.companyId} isAdmin={isAdmin} />
      )}
    </Stack>
  );
};

export default DeviationSettings;

interface DeviationFormProps {
  companyId: number | undefined;
  /** Only admins can create or change deviation types. */
  isAdmin: boolean;
  selectedContractDeviation?: ContractDeviationType | null;
  setSelectedContractDeviation?: React.Dispatch<React.SetStateAction<ContractDeviationType | null>>;
}

const DeviationForm: React.FC<DeviationFormProps> = ({
  companyId,
  isAdmin,
  selectedContractDeviation,
  setSelectedContractDeviation,
}) => {
  const { t } = useTranslation();

  const { data: createOrderValues } = useGetCreateOrderValues();
  const { data: contractDeviationUnits } = useGetContractDeviationUnits();

  const queryClient = useQueryClient();

  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = useForm<ContractDeviationFormInput>({
    resolver: zodResolver(ContractDeviationValidationSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    values: {
      companyId: companyId as number,
      name: selectedContractDeviation?.name || "",
      price: selectedContractDeviation?.price || 0,
      currency: selectedContractDeviation?.currency || "NOK",
      priceUnit: selectedContractDeviation?.priceUnit.toString() || "1",
      description: selectedContractDeviation?.description || "",
    },
  });

  // 244: the backend rejects negative prices.
  const saveErrorMessage = (error: unknown) =>
    getApiError(error).errorCode === 244 ? t("validation:priceNegative") : t("notifications:generic.createError");

  const { mutateAsync: createDeviation, isPending: isPendingCreate } = useMutation({
    mutationFn: async (data: ContractDeviationFormInput) => {
      return createContractDeviationType(data);
    },
    onMutate: async (data: ContractDeviationFormInput) => {
      const queryKey = ["contractDeviationTypes", companyId];
      await queryClient.cancelQueries({ queryKey });
      const snapshots = queryClient.getQueriesData<ContractDeviationType[]>({ queryKey });
      const optimisticDeviation: ContractDeviationType = {
        id: -Date.now(),
        companyId: companyId as number,
        name: data.name,
        price: data.price,
        priceUnit: Number(data.priceUnit),
        currency: data.currency as CurrencyCode,
        description: data.description,
        disabled: false,
      };
      queryClient.setQueriesData<ContractDeviationType[]>({ queryKey }, (old) =>
        old ? [...old, optimisticDeviation] : old
      );
      return { snapshots };
    },
    onSuccess: () => {
      notifications.show(
        SuccessNotificationObj(
          t("notifications:generic.success"),
          t("notifications:generic.createSuccess"),
          "deviation-created-success"
        )
      );
    },
    onError: (err, _data, context) => {
      context?.snapshots?.forEach(([queryKey, data]) => queryClient.setQueryData(queryKey, data));
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.error"), saveErrorMessage(err), "deviation-created-error")
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["contractDeviationTypes", companyId] });
    },
  });

  const { mutateAsync: updateContractDeviation, isPending: isPendingUpdate } = useMutation({
    mutationFn: async (data: ContractDeviationFormInput) => {
      return updateContractDeviationType(selectedContractDeviation?.id as number, data);
    },
    onMutate: async (data: ContractDeviationFormInput) => {
      const queryKey = ["contractDeviationTypes", companyId];
      const targetId = selectedContractDeviation?.id;
      await queryClient.cancelQueries({ queryKey });
      const snapshots = queryClient.getQueriesData<ContractDeviationType[]>({ queryKey });
      queryClient.setQueriesData<ContractDeviationType[]>({ queryKey }, (old) =>
        old
          ? old.map((d) =>
              d.id === targetId
                ? {
                    ...d,
                    name: data.name,
                    price: data.price,
                    priceUnit: Number(data.priceUnit),
                    currency: data.currency as CurrencyCode,
                    description: data.description,
                  }
                : d
            )
          : old
      );
      return { snapshots };
    },
    onSuccess: () => {
      notifications.show(
        SuccessNotificationObj(
          t("notifications:generic.success"),
          t("notifications:generic.createSuccess"),
          "deviation-updated-success"
        )
      );
    },
    onError: (err, _data, context) => {
      context?.snapshots?.forEach(([queryKey, data]) => queryClient.setQueryData(queryKey, data));
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.error"), saveErrorMessage(err), "deviation-updated-error")
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["contractDeviationTypes", companyId] });
    },
  });

  const onSubmit = async (data: ContractDeviationFormInput) => {
    if (selectedContractDeviation) {
      await updateContractDeviation(data);
    } else {
      await createDeviation(data);
    }
  };

  const resetForm = () => {
    if (setSelectedContractDeviation) {
      setSelectedContractDeviation(null);
    }
  };

  return (
    <React.Fragment>
      <form onSubmit={handleSubmit(onSubmit)}>
        <SettingsFormContent>
          <Grid>
            <Grid.Col span={12}>
              <Text size="md" strong={true} display="technical" tt="uppercase">
                {t("settings:deviationSettings.addContractDeviation")}
              </Text>
              <Text size="sm" color="secondary">
                {t("settings:deviationSettings.servicesDescription")}
              </Text>
            </Grid.Col>
            <Grid.Col span={12}>
              <Divider />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 10 }}>
              <Controller
                control={control}
                name="name"
                render={({ field, fieldState }) => {
                  return (
                    <TextInput
                      {...field}
                      id="name"
                      data-testid="deviation-name-input"
                      label={t("common:name")}
                      value={field.value || ""}
                      disabled={!isAdmin}
                      error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                    />
                  );
                }}
              />
            </Grid.Col>
            <Grid.Col span={12}>
              <Group gap={15} align="flex-start">
                <Controller
                  control={control}
                  name="price"
                  render={({ field, fieldState }) => {
                    return (
                      <NumberInput
                        {...field}
                        id="price"
                        value={field.value || 0}
                        label={t("common:price")}
                        error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                        hideControls={true}
                        min={0}
                        allowNegative={false}
                        disabled={!isAdmin}
                      />
                    );
                  }}
                />
                <Controller
                  control={control}
                  name="currency"
                  render={({ field, fieldState }) => {
                    const currencies = createOrderValues?.currencies.map((currency) => currency.value.toUpperCase());
                    return (
                      <Select
                        {...field}
                        id="currency"
                        label={t("common:currency")}
                        data={transformDataToSelectOption(currencies || [], "value", "value", t, undefined, true)}
                        value={field.value || ""}
                        disabled={!isAdmin}
                        error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                      />
                    );
                  }}
                />
                <Controller
                  control={control}
                  name="priceUnit"
                  render={({ field, fieldState }) => {
                    return (
                      <Select
                        {...field}
                        id="priceUnit"
                        label={t("settings:paymentFrequency")}
                        data={transformDataToSelectOption(
                          contractDeviationUnits || [],
                          "id",
                          "code",
                          t,
                          "enumerables:paymentIntervals"
                        )}
                        value={String(field.value)}
                        error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                        disabled={!isAdmin}
                      />
                    );
                  }}
                />
              </Group>
            </Grid.Col>
            <Grid.Col span={12}>
              <Controller
                control={control}
                name="description"
                render={({ field, fieldState }) => {
                  return (
                    <TextInput
                      {...field}
                      id="description"
                      data-testid="deviation-description-input"
                      label={t("common:description")}
                      value={field.value || ""}
                      error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                      disabled={!isAdmin}
                    />
                  );
                }}
              />
            </Grid.Col>
          </Grid>
        </SettingsFormContent>
        <SettingsFormCTAs>
          <CTA
            disabled={!isAdmin || !isValid}
            id="cancel"
            size="lg"
            intent={"secondary"}
            type="button"
            onClick={() => resetForm()}
          >
            {t("actions:cancel")}
          </CTA>
          <CTA
            disabled={!isAdmin || !isValid}
            id="save"
            data-testid="submit-btn"
            size="lg"
            type="submit"
            loading={isPendingCreate || isPendingUpdate}
          >
            {t("actions:save")}
          </CTA>
        </SettingsFormCTAs>
      </form>
    </React.Fragment>
  );
};
